/*!
 * XMADE · motion foundation (vanilla, no dependencies besides optional window.Lenis)
 * Implements the motion attribute contracts from XMADE-DESIGN.md:
 *   data-xm-reveal(-delay) · data-xm-stagger · data-xm-split · data-xm-words-scroll
 *   data-xm-image-reveal · data-xm-parallax · data-xm-counter · data-xm-marquee
 *   data-xm-hscroll · data-xm-sticky-steps · data-xm-magnetic · data-xm-cursor
 * API: window.XMADE = { lenis, init(root), refresh(), scrollTo(target, options) }
 */
(function () {
  'use strict';

  var win = window;
  var doc = document;
  var root = doc.documentElement;

  if (win.XMADE && win.XMADE.__booted) return;

  /* ======================================================================
     Utilities
     ====================================================================== */
  function mq(query) {
    try {
      return win.matchMedia(query);
    } catch (e) {
      return { matches: false };
    }
  }
  function onMQ(m, fn) {
    if (!m) return;
    if (m.addEventListener) m.addEventListener('change', fn);
    else if (m.addListener) m.addListener(fn);
  }
  function clamp(v, min, max) {
    return v < min ? min : v > max ? max : v;
  }
  function debounce(fn, ms) {
    var t = 0;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, ms);
    };
  }
  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }
  /* root itself (if it matches) + descendants */
  function qsa(scope, selector) {
    var out = [];
    if (!scope) return out;
    if (scope.nodeType === 1 && scope.matches && scope.matches(selector)) out.push(scope);
    if (scope.querySelectorAll) out = out.concat(toArray(scope.querySelectorAll(selector)));
    return out;
  }
  function safe(fn, ctx, args) {
    try {
      return fn.apply(ctx, args || []);
    } catch (e) {
      /* never break the storefront because of motion */
      return undefined;
    }
  }
  function attrNum(el, name, fallback) {
    var v = parseFloat(el.getAttribute(name));
    return isFinite(v) ? v : fallback;
  }
  function inClone(el) {
    return !!(el.closest && el.closest('[data-xm-clone]'));
  }
  var now = (win.performance && performance.now) ? function () { return performance.now(); } : function () { return Date.now(); };
  var supportsTranslateProp = 'translate' in root.style;

  var mqReduced = mq('(prefers-reduced-motion: reduce)');
  var mqDesktop = mq('(min-width: 990px)');
  var mqFine = mq('(hover: hover) and (pointer: fine)');

  function isDesign() {
    return !!((win.Shopify && win.Shopify.designMode) || root.classList.contains('xm-design-mode'));
  }
  function isReduced() {
    return !!mqReduced.matches;
  }
  function motionOn() {
    return !isReduced() && !isDesign();
  }

  /* ======================================================================
     State
     ====================================================================== */
  var SELECTOR = '[data-xm-reveal],[data-xm-split],[data-xm-words-scroll],[data-xm-image-reveal],' +
    '[data-xm-parallax],[data-xm-counter],[data-xm-marquee],[data-xm-hscroll],[data-xm-sticky-steps]';

  var vw = win.innerWidth;
  var vh = win.innerHeight;
  var lenis = null;
  var io = null;
  var ro = null;
  var observed = new Set();
  /* Elements hidden with clip-path (image reveal) never report as intersecting in
     Chromium, because IntersectionObserver applies the target's own clip-path.
     They are observed through a proxy (their parent element) instead. */
  var proxyMap = new Map();
  var staggerOf = new WeakMap();
  var seen = {
    reveal: new WeakSet(),
    split: new WeakSet(),
    words: new WeakSet(),
    image: new WeakSet(),
    counter: new WeakSet(),
    parallax: new WeakSet(),
    marquee: new WeakSet(),
    hscroll: new WeakSet(),
    steps: new WeakSet()
  };
  var items = { parallax: [], words: [], hscroll: [], steps: [], marquee: [] };
  var parallaxOn = false;
  var bootTime = now();
  var hasCurtain = !!doc.getElementById('x-page-transition');
  var api = win.XMADE || {};

  /* ======================================================================
     Header offset (anchors, scroll-padding)
     ====================================================================== */
  function headerOffset() {
    var fromVar = parseFloat(getComputedStyle(root).getPropertyValue('--height-header')) || 0;
    var header = doc.getElementById('sticky-header-content') ||
      doc.querySelector('.sticky-header') ||
      doc.querySelector('#shopify-section-header, .section-header, header');
    var h = header ? header.getBoundingClientRect().height : 0;
    var off = Math.max(fromVar, h);
    if (!isFinite(off) || off > vh * 0.4) off = 0;
    return Math.round(off);
  }

  /* ======================================================================
     Text splitting (keeps inline elements, <br>, diacritics, nbsp)
     ====================================================================== */
  var INLINE_TAGS = /^(EM|I|STRONG|B|SPAN|A|SMALL|U|S|MARK|SUP|SUB|ABBR|CITE|Q|DFN|TIME|DEL|INS|LABEL|FONT)$/;
  var WS = /^[ \t\n\r\f]+$/;

  function splitWords(el, makeWord) {
    var state = { last: null, index: 0 };
    (function walk(node) {
      toArray(node.childNodes).forEach(function (child) {
        if (child.nodeType === 3) {
          var text = child.data;
          if (!text) return;
          var parts = text.split(/([ \t\n\r\f]+)/);
          var frag = doc.createDocumentFragment();
          for (var k = 0; k < parts.length; k++) {
            var part = parts[k];
            if (!part) continue;
            if (WS.test(part)) {
              frag.appendChild(doc.createTextNode(part));
              state.last = null;
              continue;
            }
            /* fragment glued to the previous word (e.g. "</em>.") stays on its line */
            if (k === 0 && state.last) {
              state.last.appendChild(doc.createTextNode(part));
              continue;
            }
            var w = makeWord(part, state.index++);
            frag.appendChild(w.outer);
            state.last = w.inner;
          }
          child.parentNode.replaceChild(frag, child);
        } else if (child.nodeType === 1) {
          if (INLINE_TAGS.test(child.tagName) && !child.hasAttribute('data-xm-no-split')) {
            walk(child);
          } else {
            state.last = null; /* <br>, images, icons: atomic */
          }
        }
      });
    })(el);
    return state.index;
  }

  function splitHeading(el) {
    if (el.hasAttribute('data-xm-split-done')) return;
    splitWords(el, function (word, i) {
      var outer = doc.createElement('span');
      var inner = doc.createElement('span');
      outer.className = 'xm-w';
      inner.className = 'xm-w__i';
      inner.style.setProperty('--i', i);
      inner.textContent = word;
      outer.appendChild(inner);
      return { outer: outer, inner: inner };
    });
    el.setAttribute('data-xm-split-done', '');
  }

  function wrapScrollWords(el) {
    if (el.hasAttribute('data-xm-ws-done')) return toArray(el.querySelectorAll('.xm-ws'));
    splitWords(el, function (word) {
      var s = doc.createElement('span');
      s.className = 'xm-ws';
      s.textContent = word;
      return { outer: s, inner: s };
    });
    el.setAttribute('data-xm-ws-done', '');
    return toArray(el.querySelectorAll('.xm-ws'));
  }

  /* ======================================================================
     Counters (ro: 17.000 · plain: 2015)
     ====================================================================== */
  function formatNumber(value, decimals, format) {
    var fixed = Math.abs(value).toFixed(decimals);
    var parts = fixed.split('.');
    var intPart = parts[0];
    var sign = value < 0 ? '-' : '';
    if (format === 'plain') {
      return sign + intPart + (decimals ? '.' + parts[1] : '');
    }
    intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return sign + intPart + (decimals ? ',' + parts[1] : '');
  }

  function findNumberNode(el) {
    var walker = doc.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var n;
    while ((n = walker.nextNode())) {
      if (/\d/.test(n.data)) return n;
    }
    return null;
  }

  function setupCounter(el, motion) {
    var raw = String(el.getAttribute('data-xm-counter') || '').trim();
    if (/^\d{1,3}(\.\d{3})+$/.test(raw)) raw = raw.replace(/\./g, '');
    raw = raw.replace(',', '.');
    var target = parseFloat(raw);
    if (!isFinite(target)) return;
    var c = {
      target: target,
      decimals: (raw.split('.')[1] || '').length,
      format: (el.getAttribute('data-xm-format') || 'ro').toLowerCase(),
      prefix: el.getAttribute('data-xm-prefix') || '',
      suffix: el.getAttribute('data-xm-suffix') || '',
      duration: attrNum(el, 'data-xm-duration', 1800),
      node: findNumberNode(el),
      before: '',
      after: '',
      running: false
    };
    if (c.node) {
      var m = c.node.data.match(/\d[\d.,  ]*\d|\d/);
      if (m) {
        c.before = c.node.data.slice(0, m.index);
        c.after = c.node.data.slice(m.index + m[0].length);
        if (c.prefix && c.before.indexOf(c.prefix) === -1) c.before += c.prefix;
        if (c.suffix && c.after.indexOf(c.suffix) === -1) c.after = c.suffix + c.after;
      } else {
        c.node = null;
      }
    }
    el.__xmCounter = c;
    if (motion) renderCounter(el, c, 0);
  }

  function renderCounter(el, c, value) {
    var num = formatNumber(value, c.decimals, c.format);
    if (c.node) c.node.data = c.before + num + c.after;
    else el.textContent = c.prefix + num + c.suffix;
  }

  function runCounter(el, delay) {
    var c = el.__xmCounter;
    if (!c || c.running) return;
    c.running = true;
    setTimeout(function () {
      var start = null;
      function step(ts) {
        if (start === null) start = ts;
        var t = clamp((ts - start) / c.duration, 0, 1);
        var eased = t >= 1 ? 1 : 1 - Math.pow(2, -10 * t);
        renderCounter(el, c, c.target * eased);
        if (t < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    }, delay || 0);
  }

  /* ======================================================================
     Reveal family (one IntersectionObserver)
     ====================================================================== */
  function getIO() {
    if (io || !('IntersectionObserver' in win)) return io;
    io = new IntersectionObserver(onIntersect, {
      /* 0.05 lets very tall elements (ratio never reaching .15) pass the 25%-of-viewport test */
      threshold: [0, 0.05, 0.15, 0.3],
      rootMargin: '0px 0px -4% 0px'
    });
    return io;
  }

  function baseDelay(el) {
    return attrNum(el, 'data-xm-reveal-delay', 0);
  }

  function onIntersect(entries) {
    var batch = [];
    entries.forEach(function (en) {
      var rootH = (en.rootBounds && en.rootBounds.height) || vh;
      var b = en.boundingClientRect;
      /* the last clause covers items peeking out of horizontal scrollers
         (chips, rails, hscroll cards): vertically in view, horizontally clipped */
      var visible = en.isIntersecting &&
        (en.intersectionRatio >= 0.15 || en.intersectionRect.height >= rootH * 0.25 ||
          (b.top < rootH * 0.9 && b.bottom > rootH * 0.1));
      var passed = !en.isIntersecting && en.boundingClientRect.bottom < 0;
      if (!(visible || passed)) return;
      var t = en.target;
      var proxied = proxyMap.get(t);
      if (proxied) {
        proxyMap.delete(t);
        proxied.forEach(function (el) { batch.push({ el: el, instant: passed }); });
      }
      if (observed.has(t)) batch.push({ el: t, instant: passed });
      else if (io && proxied) io.unobserve(t);
    });
    if (!batch.length) return;

    batch.sort(function (a, b) {
      return a.el.compareDocumentPosition(b.el) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
    });

    var perParent = new Map();
    var curtain = hasCurtain && now() - bootTime < 900 ? 220 : 0;

    batch.forEach(function (b) {
      var el = b.el;
      if (io) io.unobserve(el);
      observed.delete(el);
      var delay = baseDelay(el);
      var parent = staggerOf.get(el);
      if (parent && el.hasAttribute('data-xm-reveal')) {
        var n = perParent.get(parent) || 0;
        perParent.set(parent, n + 1);
        delay += Math.min(n, 10) * attrNum(parent, 'data-xm-stagger', 80);
      }
      trigger(el, b.instant ? 0 : delay + curtain, b.instant);
    });
  }

  function trigger(el, delay, instant) {
    if (instant) el.classList.add('xm-done');
    el.style.setProperty('--xm-delay', Math.round(delay) + 'ms');
    el.classList.add('is-in');
    if (el.hasAttribute('data-xm-counter')) runCounter(el, instant ? 0 : delay);
    if (!instant && (el.hasAttribute('data-xm-reveal') || el.hasAttribute('data-xm-image-reveal'))) {
      var dur = el.hasAttribute('data-xm-image-reveal') ? 1400 : (el.getAttribute('data-xm-reveal') === 'fade' ? 1400 : 1100);
      setTimeout(function () {
        el.classList.add('xm-done');
      }, delay + dur + 80);
    }
  }

  function observeReveal(el, motion) {
    var obs = motion ? getIO() : null;
    if (!obs) {
      el.classList.add('is-in', 'xm-done');
      return;
    }
    if (el.hasAttribute('data-xm-image-reveal') && el.parentElement) {
      var proxy = el.parentElement;
      var set = proxyMap.get(proxy);
      if (!set) {
        set = new Set();
        proxyMap.set(proxy, set);
      }
      if (set.has(el)) return;
      set.add(el);
      obs.observe(proxy);
      return;
    }
    if (observed.has(el)) return;
    observed.add(el);
    obs.observe(el);
  }

  function failsafeFired() {
    var list = [];
    safe(function () { list = doc.getAnimations ? doc.getAnimations() : []; });
    for (var i = 0; i < list.length; i++) {
      if (list[i].animationName === 'xm-failsafe' && list[i].playState === 'finished') return true;
    }
    return false;
  }

  function revealInViewNow() {
    var inView = function (el) {
      var r = el.getBoundingClientRect();
      return r.bottom > 0 && r.top < vh && r.width > 0;
    };
    observed.forEach(function (el) {
      if (!inView(el)) return;
      if (io) io.unobserve(el);
      observed.delete(el);
      el.classList.add('is-in', 'xm-done');
      if (el.__xmCounter) renderCounter(el, el.__xmCounter, el.__xmCounter.target);
    });
    proxyMap.forEach(function (set, proxy) {
      set.forEach(function (el) {
        if (!inView(el)) return;
        el.classList.add('is-in', 'xm-done');
        set.delete(el);
      });
      if (!set.size) {
        if (io) io.unobserve(proxy);
        proxyMap.delete(proxy);
      }
    });
  }

  function revealAllNow() {
    observed.forEach(function (el) {
      if (io) io.unobserve(el);
      el.classList.add('is-in', 'xm-done');
      if (el.__xmCounter) renderCounter(el, el.__xmCounter, el.__xmCounter.target);
    });
    observed.clear();
    proxyMap.forEach(function (set, proxy) {
      if (io) io.unobserve(proxy);
      set.forEach(function (el) { el.classList.add('is-in', 'xm-done'); });
    });
    proxyMap.clear();
  }

  /* ======================================================================
     Marquee
     ====================================================================== */
  function prepClone(clone) {
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-xm-clone', '');
    clone.setAttribute('inert', '');
    qsa(clone, '[id]').forEach(function (n) { n.removeAttribute('id'); });
    qsa(clone, 'a, button, input, select, textarea, [tabindex]').forEach(function (n) {
      n.setAttribute('tabindex', '-1');
    });
    qsa(clone, '[data-xm-reveal],[data-xm-split],[data-xm-image-reveal]').forEach(function (n) {
      n.classList.add('is-in', 'xm-done');
    });
  }

  function setupMarquee(it) {
    var track = it.el.querySelector('.xm-marquee__track');
    if (!track) return;
    var groups = toArray(track.children).filter(function (c) {
      return c.classList.contains('xm-marquee__group');
    });
    var base = groups[0];
    if (!base) return;
    it.track = track;
    it.base = base;
    var gw = base.getBoundingClientRect().width;
    if (!gw) return; /* not laid out yet (hidden / fonts) — retried on refresh */
    var cw = it.el.clientWidth || vw;
    var k = Math.min(24, Math.max(1, Math.ceil(cw / gw)));
    var need = 2 * k;
    while (groups.length < need || groups.length % 2) {
      var clone = base.cloneNode(true);
      prepClone(clone);
      track.appendChild(clone);
      groups.push(clone);
    }
    /* data-xm-speed = seconds for one group to travel its own width,
       so the pace is identical on every viewport */
    var speed = attrNum(it.el, 'data-xm-speed', 40);
    it.el.style.setProperty('--xm-marquee-duration', (speed * (groups.length / 2)) + 's');
    it.anim = null;
    it.ready = true;
  }

  function marqueeAnimation(it) {
    if (it.anim && it.anim.playState !== 'idle') return it.anim;
    if (!it.track || !it.track.getAnimations) return null;
    var list = it.track.getAnimations();
    for (var i = 0; i < list.length; i++) {
      if (list[i].animationName === 'xm-marquee') {
        it.anim = list[i];
        return it.anim;
      }
    }
    return null;
  }

  var vel = 0;
  var velTarget = 0;
  var velRaf = 0;

  function feedVelocity(v) {
    if (!items.marquee.length || !motionOn()) return;
    velTarget = clamp(v || 0, -80, 80);
    if (!velRaf) velRaf = requestAnimationFrame(velocityTick);
  }

  function velocityTick() {
    velRaf = 0;
    vel += (velTarget - vel) * 0.12;
    velTarget *= 0.86;
    var mag = Math.abs(vel);
    var settled = mag < 0.02 && Math.abs(velTarget) < 0.02;
    var rate = settled ? 1 : 1 + Math.min(mag / 10, 3);
    var skew = settled ? 0 : clamp(vel * 0.06, -2.5, 2.5);
    items.marquee.forEach(function (it) {
      if (!it.ready) return;
      var a = marqueeAnimation(it);
      if (a) safe(function () { a.playbackRate = rate; });
      it.el.style.setProperty('--xm-velocity', skew.toFixed(3));
    });
    if (settled) {
      vel = 0;
      velTarget = 0;
    } else {
      velRaf = requestAnimationFrame(velocityTick);
    }
  }

  /* ======================================================================
     Horizontal pinned scroll
     ====================================================================== */
  function setupHscroll(it) {
    var el = it.el;
    it.track = el.querySelector('[data-xm-hscroll-track]') || el.querySelector('.xm-hscroll__track');
    it.sticky = el.querySelector('.xm-hscroll__sticky') || (it.track && it.track.parentElement);
    if (!it.track || !it.sticky) return;
    var pinned = mqDesktop.matches && motionOn();
    it.pinned = pinned;
    el.classList.toggle('xm-hscroll--pinned', pinned);
    el.classList.toggle('xm-hscroll--native', !pinned);
    if (pinned) {
      it.sticky.scrollLeft = 0;
      it.dist = Math.max(0, it.track.scrollWidth - it.sticky.clientWidth);
      el.style.height = (it.dist + it.sticky.clientHeight) + 'px';
    } else {
      it.dist = 0;
      el.style.height = '';
      it.track.style.transform = '';
      el.style.removeProperty('--xm-hscroll-progress');
    }
  }

  function updateHscroll(it) {
    if (!it.pinned || !it.track) return;
    var el = it.el;
    var r = el.getBoundingClientRect();
    var total = r.height - it.sticky.clientHeight;
    var p = total > 0 ? clamp(-r.top / total, 0, 1) : 0;
    if (p === it.p) return;
    it.p = p;
    it.track.style.transform = 'translate3d(' + (-p * it.dist).toFixed(2) + 'px,0,0)';
    el.style.setProperty('--xm-hscroll-progress', p.toFixed(4));
  }

  function bindHscrollFocus(it) {
    it.el.addEventListener('focusin', function (e) {
      if (!it.pinned || !it.track || !it.dist) return;
      var card = e.target;
      while (card && card.parentElement && card.parentElement !== it.track) card = card.parentElement;
      if (!card || card.parentElement !== it.track) return;
      it.sticky.scrollLeft = 0;
      var x = card.getBoundingClientRect().left - it.track.getBoundingClientRect().left;
      var p = clamp((x - vw * 0.1) / it.dist, 0, 1);
      var top = it.el.getBoundingClientRect().top + win.pageYOffset;
      var total = it.el.offsetHeight - it.sticky.clientHeight;
      scrollToTarget(top + p * total, { immediate: true, offset: 0 });
    });
  }

  /* ======================================================================
     Sticky steps
     ====================================================================== */
  function setupSteps(it) {
    it.steps = qsa(it.el, '[data-xm-step]').filter(function (n) { return n !== it.el; });
    it.media = qsa(it.el, '[data-xm-step-media]').filter(function (n) { return n !== it.el; });
    it.key = null;
  }

  function stepKey(node, i) {
    var v = node.getAttribute('data-xm-step');
    return v === null || v === '' ? String(i) : v;
  }

  function updateSteps(it) {
    var steps = it.steps;
    if (!steps || !steps.length) return;
    var mid = vh / 2;
    var activeIndex = 0;
    var firstTop = 0;
    var lastBottom = 0;
    for (var i = 0; i < steps.length; i++) {
      var r = steps[i].getBoundingClientRect();
      if (i === 0) firstTop = r.top;
      if (i === steps.length - 1) lastBottom = r.bottom;
      if (r.top <= mid) activeIndex = i;
    }
    var span = lastBottom - firstTop;
    var p = span > 0 ? clamp((mid - firstTop) / span, 0, 1) : 0;
    it.el.style.setProperty('--xm-progress', p.toFixed(4));

    var key = stepKey(steps[activeIndex], activeIndex);
    if (key === it.key) return;
    it.key = key;
    it.el.setAttribute('data-xm-active', key);
    steps.forEach(function (s, idx) {
      s.classList.toggle('is-active', stepKey(s, idx) === key);
    });
    it.media.forEach(function (m, idx) {
      var mk = m.getAttribute('data-xm-step-media');
      if (mk === null || mk === '') mk = String(idx);
      m.classList.toggle('is-active', mk === key);
    });
  }

  /* ======================================================================
     Parallax & scroll-lit words
     ====================================================================== */
  function updateParallax() {
    var list = items.parallax;
    var reads = [];
    for (var i = 0; i < list.length; i++) {
      var it = list[i];
      var ref = it.el.parentElement || it.el;
      var r = ref.getBoundingClientRect();
      if (r.bottom < -vh * 0.5 || r.top > vh * 1.5) {
        reads.push(null);
        continue;
      }
      reads.push(((r.top + r.height / 2) - vh / 2) * -it.speed);
    }
    for (var j = 0; j < list.length; j++) {
      if (reads[j] === null) continue;
      list[j].el.style.transform = 'translate3d(0,' + reads[j].toFixed(2) + 'px,0)';
    }
  }

  function resetParallax() {
    items.parallax.forEach(function (it) {
      it.el.style.transform = '';
    });
  }

  function updateWords(it) {
    if (!it.words.length) return;
    var r = it.el.getBoundingClientRect();
    var start = vh * 0.85;
    var end = vh * 0.3;
    var p = clamp((start - r.top) / ((start - end) + r.height), 0, 1);
    var lit = Math.round(p * it.words.length);
    if (lit === it.lit) return;
    var from = Math.min(lit, it.lit);
    var to = Math.max(lit, it.lit);
    for (var i = from; i < to; i++) {
      it.words[i].classList.toggle('is-lit', i < lit);
    }
    it.lit = lit;
  }

  /* ======================================================================
     Scroll loop (Lenis "scroll" event or rAF-throttled native scroll)
     ====================================================================== */
  var rafId = 0;
  var prevY = win.pageYOffset || 0;
  var dirAnchor = prevY;
  var dirState = 0;

  function setClass(name, on) {
    if (root.classList.contains(name) !== on) root.classList.toggle(name, on);
  }

  function updateScrollClasses(y) {
    setClass('xm-scrolled', y > 8);
    if (y <= 8) {
      dirState = 0;
      dirAnchor = y;
      setClass('xm-scroll-down', false);
      setClass('xm-scroll-up', false);
      return;
    }
    var dy = y - dirAnchor;
    if (Math.abs(dy) > 6) {
      var d = dy > 0 ? 1 : -1;
      dirAnchor = y;
      if (d !== dirState) {
        dirState = d;
        setClass('xm-scroll-down', d > 0);
        setClass('xm-scroll-up', d < 0);
      }
    }
  }

  function update() {
    var y = win.pageYOffset || root.scrollTop || 0;
    var v = lenis ? lenis.velocity : (y - prevY);
    prevY = y;
    updateScrollClasses(y);
    if (parallaxOn && items.parallax.length) updateParallax();
    for (var i = 0; i < items.words.length; i++) updateWords(items.words[i]);
    for (var j = 0; j < items.hscroll.length; j++) updateHscroll(items.hscroll[j]);
    for (var k = 0; k < items.steps.length; k++) updateSteps(items.steps[k]);
    if (v) feedVelocity(v);
    if (cursorActive) recheckCursorTarget();
  }

  function requestUpdate() {
    if (rafId) return;
    rafId = requestAnimationFrame(function () {
      rafId = 0;
      safe(update);
    });
  }

  /* ======================================================================
     Smooth scroll (Lenis) + scroll locking + anchors
     ====================================================================== */
  var locks = {};
  var alpineHooked = false;

  function setLock(key, on) {
    if (on) locks[key] = true;
    else delete locks[key];
    if (!lenis) return;
    var any = Object.keys(locks).length > 0;
    if (any && !lenis.isStopped) lenis.stop();
    else if (!any && lenis.isStopped) lenis.start();
  }

  function hookAlpine() {
    if (alpineHooked) return;
    var A = win.Alpine;
    if (!A || typeof A.effect !== 'function' || typeof A.store !== 'function') return;
    safe(function () {
      if (!A.store('xPopup')) return;
      alpineHooked = true;
      A.effect(function () {
        var p = A.store('xPopup');
        setLock('popup', !!(p && (p.open || p.openVideoShopping)));
      });
    });
  }

  var locksWatched = false;
  function watchLocks() {
    var body = doc.body;
    if (!body) return;
    /* Eurus drawers/popups add body.overflow-hidden; apps (Judge.me lightbox,
       APO popups, chat widgets) usually lock with an inline overflow style */
    var check = function () {
      var bs = body.style;
      var rs = root.style;
      setLock('body', body.classList.contains('overflow-hidden') ||
        bs.overflow === 'hidden' || bs.overflowY === 'hidden' ||
        rs.overflow === 'hidden' || rs.overflowY === 'hidden');
    };
    if (locksWatched) {
      check();
      return;
    }
    locksWatched = true;
    var mo2 = new MutationObserver(check);
    mo2.observe(body, { attributes: true, attributeFilter: ['class', 'style'] });
    mo2.observe(root, { attributes: true, attributeFilter: ['style'] });
    check();
    doc.addEventListener('alpine:initialized', hookAlpine);
    if (win.Alpine && win.Alpine.version) hookAlpine();
  }

  function initLenis() {
    if (lenis || typeof win.Lenis !== 'function' || !motionOn()) return;
    try {
      lenis = new win.Lenis({
        autoRaf: true,
        lerp: 0.1,
        smoothWheel: true,
        syncTouch: false,
        /* in-page anchors are routed by onAnchorClick (header offset, respects preventDefault) */
        anchors: false,
        allowNestedScroll: true,
        prevent: function (n) {
          return !!(n && n.closest && n.closest('[data-lenis-prevent], .scrollbar-body:not(body), .overflow-y-auto, .overflow-auto, .tree-menu-scroll, #CartDrawer, .x-cart-drawer, select'));
        }
      });
    } catch (e) {
      lenis = null;
      return;
    }
    api.lenis = lenis;
    lenis.on('scroll', function () {
      safe(update);
    });
    watchLocks();
  }

  function destroyLenis() {
    if (!lenis) return;
    safe(function () { lenis.destroy(); });
    lenis = null;
    api.lenis = null;
    toArray(root.classList).forEach(function (c) {
      if (c === 'lenis' || c.indexOf('lenis-') === 0) root.classList.remove(c);
    });
    /* scroll-driven features (steps, words, hscroll) now need native scroll events */
    win.addEventListener('scroll', requestUpdate, { passive: true });
  }

  /* Resolve a target to an absolute Y. Elements honour their own scroll-margin-top.
     A numeric Y is always handed to Lenis: for element targets Lenis would otherwise
     subtract the root scroll-padding-top (= --xm-header-offset) and the element's
     scroll-margin on top of our own offset (double header offset). */
  function resolveTarget(target) {
    if (typeof target === 'string') {
      if (target === '#top' || target === 'top') return { y: 0, el: null };
      var found = null;
      safe(function () {
        found = target.charAt(0) === '#' ? doc.getElementById(target.slice(1)) : doc.querySelector(target);
      });
      target = found;
    }
    if (typeof target === 'number') return isFinite(target) ? { y: target, el: null } : null;
    if (!target || target.nodeType !== 1) return null;
    var margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
    return { y: target.getBoundingClientRect().top + win.pageYOffset - margin, el: target };
  }

  function scrollToTarget(target, options) {
    options = options || {};
    var res = resolveTarget(target);
    if (!res) return;
    var offset = typeof options.offset === 'number' ? options.offset : (res.el ? -headerOffset() : 0);
    var y = Math.max(0, res.y + offset);
    if (lenis) {
      var o = {};
      for (var key in options) o[key] = options[key];
      o.offset = 0;
      lenis.scrollTo(y, o);
      return;
    }
    win.scrollTo({ top: y, behavior: options.immediate || isReduced() ? 'auto' : 'smooth' });
    if (typeof options.onComplete === 'function') setTimeout(options.onComplete, 600);
  }

  function onAnchorClick(e) {
    if (!lenis || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a || a.hasAttribute('data-xm-native-scroll') || a.hasAttribute('download')) return;
    if (a.target && a.target !== '_self') return;
    var href = a.getAttribute('href') || '';
    if (href.indexOf('#') === -1) return;
    var url;
    try {
      url = new URL(a.href, win.location.href);
    } catch (err) {
      return;
    }
    var loc = win.location;
    if (url.origin !== loc.origin || url.pathname !== loc.pathname || url.search !== loc.search) return;
    var hash = url.hash;
    if (!hash || hash === '#') return;
    var id = hash.slice(1);
    try {
      id = decodeURIComponent(id);
    } catch (err2) { /* keep raw */ }
    var target = doc.getElementById(id) || doc.getElementsByName(id)[0] || null;
    if (!target && id.toLowerCase() === 'top') target = 0;
    if (target === null) return;
    if (target !== 0 && !target.getClientRects().length) return;
    if (lenis.isStopped) return;
    e.preventDefault();
    var corrected = false;
    scrollToTarget(target, {
      offset: target === 0 ? 0 : -headerOffset(),
      onComplete: function () {
        /* lazy media above the target can change its position during the glide */
        if (!corrected && target && target.nodeType === 1 && lenis && !lenis.isStopped) {
          corrected = true;
          var res = resolveTarget(target);
          var want = res ? Math.max(0, Math.round(res.y - headerOffset())) : null;
          if (want !== null && Math.abs(want - (win.pageYOffset || 0)) > 3) {
            lenis.scrollTo(want, { duration: 0.6 });
          }
        }
        if (target && target.nodeType === 1 &&
          (target.hasAttribute('tabindex') || /^(A|BUTTON|INPUT|SELECT|TEXTAREA|SUMMARY)$/.test(target.tagName))) {
          safe(function () { target.focus({ preventScroll: true }); });
        }
      }
    });
    if (win.history && history.pushState) {
      safe(function () { history.pushState(null, '', hash); });
    }
  }

  function alignInitialHash() {
    if (!lenis || !win.location.hash || win.location.hash.length < 2) return;
    var id = win.location.hash.slice(1);
    try { id = decodeURIComponent(id); } catch (e) { /* raw */ }
    var target = doc.getElementById(id);
    if (!target) return;
    scrollToTarget(target, { immediate: true });
  }

  /* ======================================================================
     Cursor label & magnetic (desktop, fine pointer, motion on)
     ====================================================================== */
  var cursor = null;
  var cursorLabel = null;
  var cursorActive = false;
  var cx = 0, cy = 0, tx = 0, ty = 0;
  var cursorRaf = 0;
  var magnetEl = null;

  function pointerFX() {
    return mqFine.matches && motionOn();
  }

  function ensureCursor() {
    if (cursor || !doc.body) return;
    cursor = doc.createElement('div');
    cursor.className = 'xm-cursor';
    cursor.setAttribute('aria-hidden', 'true');
    cursor.innerHTML = '<span class="xm-cursor__circle"><span class="xm-cursor__label"></span></span>';
    doc.body.appendChild(cursor);
    cursorLabel = cursor.querySelector('.xm-cursor__label');
  }

  function cursorTick() {
    cursorRaf = 0;
    if (!cursor) return;
    cx += (tx - cx) * 0.22;
    cy += (ty - cy) * 0.22;
    cursor.style.transform = 'translate3d(' + cx.toFixed(1) + 'px,' + cy.toFixed(1) + 'px,0)';
    if (Math.abs(tx - cx) > 0.1 || Math.abs(ty - cy) > 0.1) cursorRaf = requestAnimationFrame(cursorTick);
  }

  function setCursorTarget(t) {
    if (t) {
      ensureCursor();
      if (!cursor) return;
      var label = t.getAttribute('data-xm-cursor') || '';
      if (cursorLabel.textContent !== label) cursorLabel.textContent = label;
      if (!cursorActive) {
        cursorActive = true;
        if (!cursor.classList.contains('is-active')) {
          cx = tx;
          cy = ty;
        }
        cursor.classList.add('is-active');
      }
    } else if (cursorActive) {
      cursorActive = false;
      if (cursor) cursor.classList.remove('is-active');
    }
  }

  function recheckCursorTarget() {
    var el = doc.elementFromPoint(tx, ty);
    setCursorTarget(el && el.closest ? el.closest('[data-xm-cursor]') : null);
  }

  function releaseMagnet(m) {
    m.classList.remove('is-magnet');
    if (supportsTranslateProp) m.style.translate = '';
    else m.style.transform = '';
  }

  function onPointerMove(e) {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    if (!pointerFX()) return;
    tx = e.clientX;
    ty = e.clientY;
    var target = e.target && e.target.closest ? e.target : null;

    /* cursor label */
    setCursorTarget(target ? target.closest('[data-xm-cursor]') : null);
    if (cursor && !cursorRaf) cursorRaf = requestAnimationFrame(cursorTick);

    /* magnetic */
    var m = target ? target.closest('[data-xm-magnetic]') : null;
    if (magnetEl && magnetEl !== m) {
      releaseMagnet(magnetEl);
      magnetEl = null;
    }
    if (!m) return;
    magnetEl = m;
    m.classList.add('is-magnet');
    var r = m.getBoundingClientRect();
    var cur = m.__xmMag || { x: 0, y: 0 };
    var centerX = r.left - cur.x + r.width / 2;
    var centerY = r.top - cur.y + r.height / 2;
    var mx = clamp((e.clientX - centerX) * 0.3, -8, 8);
    var my = clamp((e.clientY - centerY) * 0.3, -8, 8);
    m.__xmMag = { x: mx, y: my };
    if (supportsTranslateProp) m.style.translate = mx.toFixed(2) + 'px ' + my.toFixed(2) + 'px';
    else m.style.transform = 'translate3d(' + mx.toFixed(2) + 'px,' + my.toFixed(2) + 'px,0)';
  }

  function onPointerLeaveDoc() {
    setCursorTarget(null);
    if (magnetEl) {
      releaseMagnet(magnetEl);
      magnetEl.__xmMag = { x: 0, y: 0 };
      magnetEl = null;
    }
  }

  /* ======================================================================
     init(root) — idempotent per element
     ====================================================================== */
  function init(scope) {
    scope = scope || doc;
    if (scope.nodeType !== 1 && scope.nodeType !== 9) return;
    var motion = motionOn();
    var added = false;

    /* reveal (+ stagger parents) */
    qsa(scope, '[data-xm-reveal]').forEach(function (el) {
      if (seen.reveal.has(el) || inClone(el)) return;
      seen.reveal.add(el);
      var parent = el.parentElement && el.parentElement.closest('[data-xm-stagger]');
      if (parent) staggerOf.set(el, parent);
      observeReveal(el, motion);
    });

    /* split headings */
    qsa(scope, '[data-xm-split]').forEach(function (el) {
      if (seen.split.has(el) || inClone(el)) return;
      seen.split.add(el);
      if (motion) safe(splitHeading, null, [el]);
      observeReveal(el, motion);
    });

    /* image reveal */
    qsa(scope, '[data-xm-image-reveal]').forEach(function (el) {
      if (seen.image.has(el) || inClone(el)) return;
      seen.image.add(el);
      var t = el.querySelector('img, video, .xm-media__inner');
      if (t) t.classList.add('xm-ir-target');
      observeReveal(el, motion);
    });

    /* counters */
    qsa(scope, '[data-xm-counter]').forEach(function (el) {
      if (seen.counter.has(el) || inClone(el)) return;
      seen.counter.add(el);
      safe(setupCounter, null, [el, motion]);
      observeReveal(el, motion);
    });

    /* scroll-lit words */
    qsa(scope, '[data-xm-words-scroll]').forEach(function (el) {
      if (seen.words.has(el) || inClone(el)) return;
      seen.words.add(el);
      if (!motion) return;
      var words = safe(wrapScrollWords, null, [el]) || [];
      items.words.push({ el: el, words: words, lit: 0 });
      added = true;
    });

    /* parallax */
    qsa(scope, '[data-xm-parallax]').forEach(function (el) {
      if (seen.parallax.has(el) || inClone(el)) return;
      seen.parallax.add(el);
      items.parallax.push({ el: el, speed: attrNum(el, 'data-xm-parallax', 0.12) });
      added = true;
    });

    /* marquee */
    qsa(scope, '[data-xm-marquee]').forEach(function (el) {
      if (seen.marquee.has(el) || inClone(el)) return;
      seen.marquee.add(el);
      var it = { el: el, ready: false };
      items.marquee.push(it);
      safe(setupMarquee, null, [it]);
      if (ro && it.base) {
        ro.observe(it.base);
        it.observedBase = true;
      }
      added = true;
    });

    /* pinned horizontal scroll */
    qsa(scope, '[data-xm-hscroll]').forEach(function (el) {
      if (seen.hscroll.has(el) || inClone(el)) return;
      seen.hscroll.add(el);
      var it = { el: el, p: -1 };
      items.hscroll.push(it);
      safe(setupHscroll, null, [it]);
      bindHscrollFocus(it);
      if (ro && it.track) ro.observe(it.track);
      added = true;
    });

    /* sticky steps */
    qsa(scope, '[data-xm-sticky-steps]').forEach(function (el) {
      if (seen.steps.has(el) || inClone(el)) return;
      seen.steps.add(el);
      var it = { el: el };
      safe(setupSteps, null, [it]);
      items.steps.push(it);
      added = true;
    });

    if (added) safe(update);
  }

  /* remove registry entries / observers inside a root (section unload, DOM removal) */
  function cleanup(scope) {
    var gone = function (el) {
      return !el.isConnected || (scope && scope !== doc && scope.contains(el));
    };
    Object.keys(items).forEach(function (key) {
      items[key] = items[key].filter(function (it) {
        if (!gone(it.el)) return true;
        if (ro) {
          if (it.base) ro.unobserve(it.base);
          if (it.track) ro.unobserve(it.track);
        }
        return false;
      });
    });
    observed.forEach(function (el) {
      if (gone(el)) {
        if (io) io.unobserve(el);
        observed.delete(el);
      }
    });
    proxyMap.forEach(function (set, proxy) {
      if (gone(proxy)) {
        if (io) io.unobserve(proxy);
        proxyMap.delete(proxy);
      }
    });
    if (magnetEl && gone(magnetEl)) magnetEl = null;
  }

  /* ======================================================================
     refresh() — recompute sizes
     ====================================================================== */
  function refresh() {
    vw = win.innerWidth;
    vh = win.innerHeight;
    safe(function () {
      root.style.setProperty('--xm-header-offset', headerOffset() + 'px');
    });
    items.marquee.forEach(function (it) {
      safe(setupMarquee, null, [it]);
      if (ro && it.base && !it.observedBase) {
        ro.observe(it.base);
        it.observedBase = true;
      }
    });
    items.hscroll.forEach(function (it) {
      it.p = -1;
      safe(setupHscroll, null, [it]);
    });
    var wasOn = parallaxOn;
    parallaxOn = mqDesktop.matches && motionOn();
    if (wasOn && !parallaxOn) resetParallax();
    if (lenis) safe(function () { lenis.resize(); });
    safe(update);
  }

  /* ======================================================================
     Boot
     ====================================================================== */
  var mo = null;
  var pendingRoots = [];
  var pendingPrune = false;
  var moTimer = 0;

  function isOwnNode(n) {
    var cl = n.classList;
    return !!cl && (cl.contains('xm-w') || cl.contains('xm-w__i') || cl.contains('xm-ws') ||
      cl.contains('xm-cursor') || n.hasAttribute('data-xm-clone'));
  }

  function flushMutations() {
    moTimer = 0;
    if (pendingPrune) {
      pendingPrune = false;
      cleanup(null);
    }
    var roots = pendingRoots;
    pendingRoots = [];
    var any = false;
    roots.forEach(function (n) {
      if (!n.isConnected) return;
      if ((n.matches && n.matches(SELECTOR)) || (n.querySelector && n.querySelector(SELECTOR))) {
        init(n);
        any = true;
      }
    });
    if (any) refresh();
  }

  function watchMutations() {
    if (!('MutationObserver' in win) || !doc.body) return;
    mo = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.removedNodes && m.removedNodes.length) pendingPrune = true;
        for (var j = 0; j < m.addedNodes.length; j++) {
          var n = m.addedNodes[j];
          if (n.nodeType === 1 && !isOwnNode(n)) pendingRoots.push(n);
        }
      }
      if ((pendingRoots.length || pendingPrune) && !moTimer) moTimer = setTimeout(flushMutations, 120);
    });
    mo.observe(doc.body, { childList: true, subtree: true });
  }

  function onReducedChange() {
    setClass('xm-reduced', isReduced());
    if (isReduced()) {
      destroyLenis();
      revealAllNow();
      resetParallax();
      setCursorTarget(null);
    } else if (!lenis && motionOn()) {
      safe(initLenis);
      if (lenis) win.removeEventListener('scroll', requestUpdate, { passive: true });
    }
    /* never leave `cursor: none` behind once the custom cursor is disabled */
    setClass('xm-cursor-on', pointerFX());
    refresh();
  }

  function boot() {
    if (win.Shopify && win.Shopify.designMode) setClass('xm-design-mode', true);
    setClass('xm-reduced', isReduced());

    if ('ResizeObserver' in win) {
      ro = new ResizeObserver(debounce(function () { safe(refresh); }, 120));
    }

    safe(initLenis);
    safe(init, null, [doc]);
    /* Slow connection: the CSS fail-safe (3s) has already shown the content.
       Keep what is on screen visible instead of hiding it again to animate. */
    if (now() > 2900 && failsafeFired()) safe(revealInViewNow);
    setClass('xm-ready', true);

    /* scroll */
    if (!lenis) win.addEventListener('scroll', requestUpdate, { passive: true });

    /* sizes */
    win.addEventListener('resize', debounce(function () { safe(refresh); }, 150), { passive: true });
    win.addEventListener('load', function () {
      safe(refresh);
      safe(alignInitialHash);
    });
    if (doc.fonts && doc.fonts.ready) {
      doc.fonts.ready.then(function () { safe(refresh); }, function () {});
    }
    onMQ(mqReduced, onReducedChange);
    onMQ(mqDesktop, function () { safe(refresh); });
    onMQ(mqFine, function () {
      setClass('xm-cursor-on', pointerFX());
      if (!pointerFX()) setCursorTarget(null);
    });

    /* anchors, pointer FX */
    doc.addEventListener('click', onAnchorClick, false);
    doc.addEventListener('pointermove', onPointerMove, { passive: true });
    root.addEventListener('mouseleave', onPointerLeaveDoc);
    if (pointerFX()) setClass('xm-cursor-on', true);

    /* theme editor */
    doc.addEventListener('shopify:section:load', function (e) {
      safe(init, null, [e.target]);
      safe(refresh);
    });
    doc.addEventListener('shopify:section:unload', function (e) {
      safe(cleanup, null, [e.target]);
    });
    doc.addEventListener('shopify:section:reorder', function () { safe(refresh); });

    /* Third-party cart changes (e.g. the Advanced Product Options app heals add-on
       quantities with /cart/change.js and then fires 'cart:updated' / 'cart:refresh').
       Eurus only listens to its own 'eurus:cart:reload', so relay those events —
       debounced, so a burst of app events triggers a single drawer re-render. */
    var cartRelayTimer = null;
    ['cart:updated', 'cart:refresh'].forEach(function (name) {
      doc.addEventListener(name, function () {
        clearTimeout(cartRelayTimer);
        cartRelayTimer = setTimeout(function () {
          try { doc.dispatchEvent(new CustomEvent('eurus:cart:reload')); } catch (e) { /* noop */ }
        }, 250);
      });
    });

    /* AJAX-inserted content (product grids, drawers) */
    watchMutations();

    safe(refresh);

    try {
      doc.dispatchEvent(new CustomEvent('xmade:ready', { detail: api }));
    } catch (e) { /* old browsers */ }
  }

  /* ======================================================================
     Public API
     ====================================================================== */
  api.lenis = null;
  api.init = function (scope) {
    safe(init, null, [scope || doc]);
  };
  api.refresh = function () {
    safe(refresh);
  };
  api.scrollTo = function (target, options) {
    safe(scrollToTarget, null, [target, options]);
  };
  api.__booted = true;
  win.XMADE = api;

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', function () { safe(boot); });
  } else {
    safe(boot);
  }
})();
